import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink, FiColumns } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { listInstalledWidgets, updateInstallationConfig, widgetHasSurface, type InstalledWidget } from '../../services/myToolsApi';
import WidgetFrame from './WidgetFrame';
import { withPaused } from './widgetInstallControls';
import { useWidgetInstallsChanged } from './widgetEvents';
import { useToast } from '../../context/ToastContext';
import { summarizeWidgetPermissions } from './widgetPermissions';
import { WIDGET_STARTER_FILENAME, WIDGET_STARTER_URL } from './widgetSdkLinks';
import '../../styles/Widgets.css';

const WidgetsPage: React.FC = () => {
  const { toolId } = useParams<{ toolId?: string }>();
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState<InstalledWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { showToast } = useToast();
  useWidgetInstallsChanged(() => setReloadKey(k => k + 1));

  const resume = async (widget: InstalledWidget) => {
    setResuming(true);
    try {
      const controls = { ...widget.controls, paused: false };
      const merged = withPaused(widget.rawConfigValues, false);
      await updateInstallationConfig(widget.id, widget.installationId, merged);
      setWidgets(prev => prev.map(w => w.id === widget.id ? { ...w, paused: false, controls, rawConfigValues: merged } : w));
    } catch (err) {
      // Not setError: that state means "the list failed to load" and replaced the whole page.
      showToast('error', err instanceof Error ? err.message : 'Failed to resume the widget');
    } finally {
      setResuming(false);
    }
  };

  // Reloads (after an install changes anywhere in this tab) keep showing the current list
  // instead of the loading state, so a running widget isn't torn down and restarted.
  useEffect(() => {
    const controller = new AbortController();
    listInstalledWidgets(controller.signal)
      .then(list => { if (!controller.signal.aborted) { setWidgets(list); setError(null); } })
      .catch(err => { if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Failed to load widgets'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [reloadKey]);

  const pinToSidebar = (id: string) => {
    window.dispatchEvent(new CustomEvent('widgets:open-in-sidebar', { detail: { toolId: id } }));
    // The app hides the right sidebar on narrow windows (layout.css), so pinning would
    // otherwise look like it did nothing.
    if (window.matchMedia('(max-width: 1024px)').matches)
      showToast('success', 'Pinned. The sidebar is hidden on narrow windows — widen the window to see it.');
  };

  if (toolId) {
    const widget = widgets.find(w => w.id === toolId);
    return (
      <div className="wg-page">
        <div className="wg-detail">
          <div className="wg-detail__bar">
            <button type="button" className="wg-detail__back" onClick={() => navigate(ROUTES.WIDGETS)}>
              <FiArrowLeft size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />Widgets
            </button>
            <div className="wg-detail__title">
              <span>{widget?.icon ?? '📊'}</span>
              <span>{widget?.name ?? 'Widget'}</span>
              {widget && <span className="wg-chip wg-chip--accent">v{widget.version}</span>}
              {widget?.paused && <span className="wg-chip wg-chip--paused">Paused</span>}
              {widget && widget.pendingPermissions.length > 0 && (
                <span className="wg-chip wg-chip--pending" title={widget.pendingPermissions.join(', ')}>
                  🔔 {widget.pendingPermissions.length} new permission{widget.pendingPermissions.length === 1 ? '' : 's'} awaiting your approval
                </span>
              )}
            </div>
            <div className="wg-page__actions" style={{ marginLeft: 'auto' }}>
              {widget && widget.pendingPermissions.length > 0 && (
                <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=installed&toolId=${widget.id}`)}>
                  Review permissions
                </button>
              )}
              {widget?.paused && (
                <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" disabled={resuming} onClick={() => resume(widget)}>
                  {resuming ? 'Resuming…' : 'Resume widget'}
                </button>
              )}
              {widget && widgetHasSurface(widget.manifest, 'sidebar') && (
                <button
                  type="button"
                  className="mt-btn mt-btn--secondary mt-btn--sm"
                  title="Open in the right sidebar"
                  onClick={() => pinToSidebar(widget.id)}
                >
                  <FiColumns size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />Pin to sidebar
                </button>
              )}
              <button
                type="button"
                className="mt-btn mt-btn--secondary mt-btn--sm"
                onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=installed`)}
              >
                Manage
              </button>
            </div>
          </div>
          <div className="wg-detail__frame">
            {loading ? (
              // Wait for the install record before starting the widget: until then pause state,
              // trimmed permissions and sandbox are unknown, so a paused widget would briefly run
              // and the frame would keep the bare sandbox even after the real one arrived.
              <div className="wg-empty"><span>Loading widget…</span></div>
            ) : error ? (
              <div className="wg-empty">
                <span className="wg-empty__icon">⚠️</span>
                <span className="wg-empty__title">Couldn't load your widgets</span>
                <span>{error}</span>
                <div className="wg-empty__actions">
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => window.location.reload()}>Try again</button>
                </div>
              </div>
            ) : !widget ? (
              // Distinguish "not installed / uninstalled elsewhere / stale link" from a genuine
              // load error inside WidgetFrame, which otherwise looks identical to this case.
              <div className="wg-empty">
                <span className="wg-empty__icon">📊</span>
                <span className="wg-empty__title">You don't have this widget installed</span>
                <span>It may have been uninstalled, or this link is out of date.</span>
                <div className="wg-empty__actions">
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>Browse Marketplace</button>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => navigate(ROUTES.WIDGETS)}>Back to Widgets</button>
                </div>
              </div>
            ) : (
              // Sized to the widget's reported content height so a small widget doesn't sit
              // in a page-tall empty frame; large widgets grow (capped) and the page scrolls.
              <WidgetFrame
                key={toolId}
                toolId={toolId}
                installationId={widget?.installationId}
                manifest={widget?.manifest ?? null}
                grantedSandbox={widget?.grantedSandbox}
                config={widget?.configValues}
                surface="page"
                height={widget?.manifest?.minHeight ?? 480}
                autoResize
                maxHeight={4000}
                paused={widget?.paused}
                pausedHint="use “Resume widget” above, or Installed → Configuration."
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  const pageWidgets = widgets.filter(w => widgetHasSurface(w.manifest, 'page'));

  return (
    <div className="wg-page">
      <div className="wg-page__header">
        <div>
          <h1 className="wg-page__title">Widgets</h1>
          <p className="wg-page__subtitle">Installed widgets from the Marketplace. Each one runs in its own sandbox.</p>
        </div>
        <div className="wg-page__actions">
          <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>
            Browse Marketplace
          </button>
          <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=create`)}>
            + Create a widget
          </button>
        </div>
      </div>

      <div className="wg-page__body">
        {loading && <div className="wg-empty"><span>Loading widgets…</span></div>}
        {!loading && error && <div className="wg-empty"><span>{error}</span></div>}
        {!loading && !error && pageWidgets.length === 0 && widgets.length > 0 && (
          <div className="wg-empty">
            <span className="wg-empty__icon">📊</span>
            <span className="wg-empty__title">No widgets for this page</span>
            <span>
              Your {widgets.length === 1 ? 'widget runs' : `${widgets.length} widgets run`} in the right sidebar or on the
              home screen instead. Change where each one shows under Installed → Configuration.
            </span>
            <div className="wg-empty__actions">
              <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=installed`)}>Open Installed</button>
            </div>
          </div>
        )}
        {!loading && !error && widgets.length === 0 && (
          <div className="wg-empty">
            <span className="wg-empty__icon">📊</span>
            <span className="wg-empty__title">No widgets installed yet</span>
            <span>Install a widget from the Marketplace, or build your own from a zip or HTML file.</span>
            <div className="wg-empty__actions">
              <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>Browse Marketplace</button>
              <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=create`)}>Create a widget</button>
              <a className="mt-btn mt-btn--secondary mt-btn--sm" href={WIDGET_STARTER_URL} download={WIDGET_STARTER_FILENAME} title="A ready-to-upload example bundle showing every SDK call, with comments">Download starter template</a>
            </div>
          </div>
        )}
        {!loading && !error && pageWidgets.length > 0 && (
          <div className="wg-grid">
            {pageWidgets.map(w => (
              <button key={w.id} type="button" className="wg-card" onClick={() => navigate(`${ROUTES.WIDGETS}/${w.id}`)}>
                <div className="wg-card__head">
                  <span className="wg-card__icon">{w.icon}</span>
                  <span className="wg-card__name">{w.name}</span>
                  {w.paused && <span className="wg-chip wg-chip--paused">Paused</span>}
                  {w.pendingPermissions.length > 0 && <span className="wg-chip wg-chip--pending" title="A newer version asks for permissions you haven't approved">🔔 {w.pendingPermissions.length} new</span>}
                  <span className="wg-chip wg-chip--accent wg-card__version">v{w.version}</span>
                </div>
                {w.publisherName && <span className="wg-card__publisher" title={`Published by ${w.publisherName}`}>by {w.publisherName}</span>}
                {w.description && <p className="wg-card__desc">{w.description}</p>}
                <div className="wg-card__foot">
                  {(w.manifest?.surfaces ?? ['page']).map(s => <span key={s} className="wg-chip">{s}</span>)}
                  <span className="wg-chip" title="Permissions this widget was installed with">🔒 {summarizeWidgetPermissions(w.manifest)}</span>
                  <span className="wg-chip" style={{ marginLeft: 'auto' }}><FiExternalLink size={11} /> Open</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetsPage;
